import React, { useEffect } from "react";
import { HERO, PLANS, STEPS, BRAND } from "../mock";
import { useAuth } from "../context/AuthContext";
import { scrollTo } from "./Nav";
import { Check, Star } from "lucide-react";
import NewsSection from "./NewsSection";
import ResultsTeaser from "./ResultsTeaser";

const tones = {
  amber: { background: "rgba(255,176,32,0.14)", color: "var(--g)" },
  gold: { background: "rgba(255,224,130,0.14)", color: "var(--gold)" },
  red: { background: "rgba(255,122,61,0.14)", color: "var(--orange)" },
};

const Landing = () => {
  const { user, openAuth, setCheckoutPlan } = useAuth();

  useEffect(() => {
    if (window.location.hash) setTimeout(() => scrollTo(window.location.hash.slice(1)), 300);
  }, []);

  const choose = (plan) => {
    setCheckoutPlan(plan);
    if (!user) openAuth("create");
  };

  return (
    <div id="landingPage">
      {/* HERO */}
      <section className="hero" data-testid="hero">
        <div className="hero-eye fade-up">{HERO.eye}</div>
        <h1 className="hero-h fade-up d1">{HERO.line1}<br /><em>{HERO.line2}</em></h1>
        <p className="hero-sub fade-up d2">{BRAND.full} AI Scanner hunts 31 markets around the clock and delivers at least 2 AI-reviewed signals a day — entry, stop loss, TP1 and TP2 already calculated.</p>
        <button className="hero-cta fade-up d3" onClick={() => scrollTo("pricing")} data-testid="hero-cta">See Pricing →</button>
        <div className="hero-proof fade-up d4">
          {HERO.proof.map((p) => (
            <div className="proof-item" key={p.lbl}>
              <div className="proof-val">{p.val}</div>
              <div className="proof-lbl">{p.lbl}</div>
            </div>
          ))}
        </div>
      </section>

      <ResultsTeaser />

      <NewsSection />

      {/* PRICING */}
      <section className="pricing" id="pricing" data-testid="pricing">
        <div className="section-eye">Pricing</div>
        <h2 className="section-h">Two Ways In.<br /><em>Same AI Scanner.</em></h2>
        <p className="section-sub">Test drive for 3 days for $5, then upgrade to Monthly for $79. Cancel anytime.</p>
        <div className="plans">
          {PLANS.map((p) => (
            <div className={`plan ${p.featured ? "featured" : ""}`} key={p.id} data-testid={`plan-${p.id}`}>
              {p.featured && <div className="popular-tag"><Star size={10} fill="currentColor" /> MOST POPULAR</div>}
              <div className="plan-badge" style={tones[p.badgeTone]}>{p.badge}</div>
              <div className="plan-name">{p.name}</div>
              <div className="plan-price">${p.price}</div>
              <div className="plan-per">{p.per}</div>
              <ul className="plan-features">
                {p.features.map((f) => (
                  <li key={f}><Check size={12} strokeWidth={3} className="chk" />{f}</li>
                ))}
              </ul>
              <button className={`plan-btn ${p.btn === "outline" ? "btn-outline" : "btn-solid"}`} onClick={() => choose(p)} data-testid={`plan-btn-${p.id}`}>{p.cta}</button>
            </div>
          ))}
        </div>
        <div className="pricing-note">
          Secure checkout by Stripe &nbsp;·&nbsp; Instant activation after payment &nbsp;·&nbsp; Questions? <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a> &nbsp;·&nbsp; Trial is one per account &nbsp;·&nbsp; All sales final — no refunds
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how" id="how" data-testid="how">
        <div className="how-inner">
          <div className="section-eye left">How It Works</div>
          <h2 className="section-h left">Simple. Fast.<br /><em>No Guessing.</em></h2>
          <div className="steps">
            {STEPS.map((s, i) => (
              <div className="step" key={s.t}>
                <div className="step-num">{i + 1}</div>
                <div>
                  <h3>{s.t}</h3>
                  <p>{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-block">
        <h2>Ready to trade<br /><em>with a real edge?</em></h2>
        <p>$5 for a 3-day trial or $79 a month. 2+ AI signals every day.</p>
        <button onClick={() => scrollTo("pricing")} data-testid="cta-choose-plan">Choose Your Plan →</button>
      </section>

      {/* REFUND */}
      <div className="refund">
        <div className="refund-eye">Refund Policy</div>
        <div className="refund-txt">All sales are final. No refunds will be issued for any reason. You may cancel monthly subscriptions anytime to stop future charges. Signals are for educational purposes only and are not financial advice. Past signal results do not guarantee future results. Trading involves substantial risk of loss.</div>
      </div>

      <footer>
        <p>{BRAND.full} &nbsp;·&nbsp; <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a></p>
        <p style={{ marginTop: 4 }}>Not financial advice. Trading involves risk. Results not typical.</p>
      </footer>

      {/* MOBILE BAR */}
      <div className="mobile-bar">
        <button className="mb-login" onClick={() => openAuth("login")}>Member Login</button>
        <button className="mb-access" onClick={() => scrollTo("pricing")}>Get Access</button>
      </div>
    </div>
  );
};

export default Landing;
